/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect, useContext } from 'react';
import DeleteIcon from '@mui/icons-material/Close';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { AppContext } from 'Context';
import Autocomplete from '@mui/material/Autocomplete';
import { Chip, TextField, Popper, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import withStyles from '@mui/styles/withStyles';
import styles from '../index.module.css';
import './newSearchInput.css';
import PersonButton from './PersonButton';

const StyledPopper = withStyles({
  root: {
    marginTop: '10px !important',
    zIndex: 1400,
    animation: 'dropdownSlideIn 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
    // White card with a hairline border — matches the new-UI surfaces instead of
    // the old blue gradient panel.
    '& .MuiAutocomplete-paper': {
      borderRadius: '14px',
      boxShadow: '0 1px 2px rgba(15,23,42,0.04), 0 18px 44px rgba(15,23,42,0.16)',
      border: '1px solid rgba(15,17,28,0.08)',
      background: '#ffffff',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-listbox': {
      // Inset so a highlighted row's rounded fill never touches the card edge.
      padding: '10px',
      maxHeight: '288px',
      '&::-webkit-scrollbar': { width: '6px' },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': {
        background: 'rgba(15,17,28,0.16)',
        borderRadius: '99px',
        '&:hover': { background: 'rgba(15,17,28,0.28)' },
      },
    },
    '& .MuiAutocomplete-option': {
      padding: '0 !important',
      minHeight: 'auto !important',
      borderRadius: '12px !important',
      marginBottom: '2px !important',
      background: 'transparent !important',
      '&:last-child': { marginBottom: '0 !important' },
      // No highlight container on the option rows — MUI's own hover/highlight
      // fills are suppressed so a row reads as plain text on the white card.
      '&[aria-selected="true"], &.Mui-focused, &:hover': {
        background: 'transparent !important',
      },
    },
    '& .MuiAutocomplete-noOptions': {
      padding: '16px',
      textAlign: 'center',
      color: '#98a2b3',
      fontFamily: '"Montserrat", sans-serif',
      fontSize: '13px',
    },
  },
})(Popper);

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    justifyContent: 'start',
    flexWrap: 'wrap',
    '& > *': {
      margin: theme.spacing(0.5),
    },
  },
  tag: {
    border: '1px solid rgba(15,17,28,0.08)',
    background: '#f7f8fa',
    fontWeight: 600,
    fontFamily: '"Montserrat", sans-serif !important',
    fontSize: '12.5px',
    height: '30px',
    borderRadius: '99px',
    color: '#15171c',
    boxShadow: 'none',
    margin: '3px !important',
    animation: 'chipSlideIn 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
    transition: 'background 0.16s ease, border-color 0.16s ease',
    '&:hover': {
      background: '#eef0f4',
      borderColor: 'rgba(15,17,28,0.14)',
    },
    '& svg': {
      color: '#98a2b3',
      fontSize: '17px !important',
      transition: 'color 0.16s ease, transform 0.16s ease',
      '&:hover': {
        color: '#ef4444',
        transform: 'scale(1.12)',
      },
    },
    '& .MuiChip-label': {
      padding: '0 4px 0 8px',
      fontWeight: 600,
      fontSize: '12.5px',
      lineHeight: 1.2,
      color: '#15171c',
    },
  },
  // Dark twin of `tag`, for the /access request-access modal. The chips render
  // inside the field so they have to invert with it.
  tagDark: {
    border: '1px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.10)',
    fontWeight: 600,
    fontFamily: '"Montserrat", sans-serif !important',
    fontSize: '12.5px',
    height: '30px',
    borderRadius: '99px',
    color: '#f5f5f7',
    boxShadow: 'none',
    margin: '3px !important',
    animation: 'chipSlideIn 0.24s cubic-bezier(0.22, 1, 0.36, 1)',
    transition: 'background 0.16s ease, border-color 0.16s ease',
    '&:hover': {
      background: 'rgba(255,255,255,0.16)',
      borderColor: 'rgba(255,255,255,0.2)',
    },
    '& svg': {
      color: 'rgba(245,245,247,0.6)',
      fontSize: '17px !important',
      transition: 'color 0.16s ease, transform 0.16s ease',
      '&:hover': {
        color: '#ff6b6b',
        transform: 'scale(1.12)',
      },
    },
    '& .MuiChip-label': {
      padding: '0 4px 0 8px',
      fontWeight: 600,
      fontSize: '12.5px',
      lineHeight: 1.2,
      color: '#f5f5f7',
    },
  },
  option: {
    height: '10px'
  }
}));

const CssTextField = withStyles({
  root: {
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
    '& .MuiInputBase-input': {
      height: 'auto',
      padding: '9px 6px !important',
    },
    '& .MuiInputBase-root': {
      color: '#15171c',
      fontFamily: '"Montserrat", sans-serif !important',
      fontSize: '13.5px',
      fontWeight: 500,
      padding: '5px 12px',
      minHeight: '48px',
      border: '1px solid rgba(15,17,28,0.12)',
      // 14px rounded rect (not a full pill) so it reads as a field, and so
      // multiple selected chips wrap cleanly inside it.
      borderRadius: '14px',
      background: '#ffffff',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
    },
    '& .MuiInputBase-root:hover': {
      borderColor: 'rgba(15,17,28,0.22)',
    },
    '& .MuiInputBase-root.Mui-focused': {
      borderColor: '#5aa5ef',
      boxShadow: '0 0 0 3px rgba(90, 165, 239, 0.16)',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#98a2b3',
      opacity: 1,
      fontSize: '13.5px',
      fontWeight: 500,
    },
  },
})(TextField);

// Dark twin of CssTextField — same geometry, inverted surface. Used by the
// dark pages' request/grant-access modals (see ShareScreen's `darkTheme`).
const DarkCssTextField = withStyles({
  root: {
    '& .MuiInput-underline:after': { borderBottom: 'none' },
    '& .MuiInput-underline:before': { borderBottom: 'none' },
    '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
    '& .MuiInputBase-input': {
      height: 'auto',
      padding: '9px 6px !important',
    },
    '& .MuiInputBase-root': {
      color: '#f5f5f7',
      fontFamily: '"Montserrat", sans-serif !important',
      fontSize: '13.5px',
      fontWeight: 500,
      padding: '5px 12px',
      minHeight: '48px',
      border: '1px solid rgba(255,255,255,0.14)',
      borderRadius: '14px',
      background: 'rgba(255,255,255,0.06)',
      transition: 'border-color 0.18s ease, box-shadow 0.18s ease',
    },
    '& .MuiInputBase-root:hover': {
      borderColor: 'rgba(255,255,255,0.26)',
    },
    '& .MuiInputBase-root.Mui-focused': {
      borderColor: 'rgba(255,255,255,0.55)',
      boxShadow: '0 0 0 3px rgba(255,255,255,0.12)',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(245,245,247,0.45)',
      opacity: 1,
      fontSize: '13.5px',
      fontWeight: 500,
    },
  },
})(TextField);

// The suggestion dropdown is portalled to <body>, so it cannot inherit the
// dialog's dark class — it is themed through Autocomplete's `paper` slot.
const darkPaperSx = {
  borderRadius: '14px',
  border: '1px solid rgba(255,255,255,0.08)',
  background: 'rgba(18,20,28,0.96)',
  backgroundImage: 'none',
  color: '#f5f5f7',
  backdropFilter: 'blur(22px)',
  WebkitBackdropFilter: 'blur(22px)',
  boxShadow: '0 20px 50px rgba(0,0,0,0.5)',
  overflow: 'hidden',
  '& .MuiAutocomplete-listbox': {
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255,255,255,0.18)',
      '&:hover': { background: 'rgba(255,255,255,0.3)' },
    },
  },
  '& .MuiAutocomplete-noOptions': { color: 'rgba(245,245,247,0.6)' },
};

export default function SearchInput({
  handlingAction, searchInputHandler, setUserSearchingStatus, cancelToken, setEmailToSearch, setUserList,
  setCancelToken, AxiosCancelToken, inputPlaceholder, selectedUsers, removeUser, searchUsers, userList, selectUser,
  removeUserFromList, selectUserFromList, emailToSearch, onEnterPress, duplicateErrorType, isSelfSelected, isDisabled,
  searchingUsers, darkTheme
}) {
  const classes = useStyles();
  const InputField = darkTheme ? DarkCssTextField : CssTextField;
  const { langText } = useContext(AppContext);
  const commonText = langText?.common;
  const [oldLength, newLength] = useState(0);

  // Filter out already selected users and ensure userList is an array
  // Don't show any suggestions while API is loading to prevent showing stale/typed email
  const uniqueUserList = searchingUsers ? [] : (Array.isArray(userList) ? userList : []).filter(
    (user) => !selectedUsers.some((selectedUser) => selectedUser.email === user.email)
  );

  const [inputValue, setInputValue] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Filter options based on input value
  const filteredOptions = React.useMemo(() => {
    if (!inputValue || inputValue.trim().length === 0) {
      return [];
    }
    return uniqueUserList.filter((option) => option.email?.toLowerCase().includes(inputValue.toLowerCase())
      || option.firstname?.toLowerCase().includes(inputValue.toLowerCase())
      || option.lastname?.toLowerCase().includes(inputValue.toLowerCase()));
  }, [inputValue, uniqueUserList]);

  const showHelperText = !inputValue || inputValue.length === 0;

  // Control dropdown visibility based on whether there are filtered options
  React.useEffect(() => {
    setOpen(filteredOptions.length > 0 && inputValue.length > 0);
  }, [filteredOptions.length, inputValue]);

  // Control loading state - only stop when search is complete
  React.useEffect(() => {
    // Stop loading when search is complete (searchingUsers becomes false)
    if (!searchingUsers) {
      setLoading(false);
    }
  }, [searchingUsers]);

  return (
    <div className={styles.shareScreenEmailInput}>
      {isSelfSelected ? (
        <div className={styles.errorText}>
          You can&apos;t invite yourself to a group
        </div>
      ) : duplicateErrorType === 'member' ? (
        <div className={styles.errorText}>
          This user is already a member of the group.
        </div>
      ) : duplicateErrorType === 'pending' ? (
        <div className={styles.errorText}>
          This user has already been invited.
        </div>
      ) : null}
      {showHelperText && (
        <div className={styles.helperText}>
          {commonText?.startTypingMsg || 'Start typing email to search'}
        </div>
      )}
      <Autocomplete
        multiple
        id="tags-standard"
        disableClearable
        forcePopupIcon={false}
        filterSelectedOptions
        autoHighlight
        open={open}
        onOpen={() => {
          if (filteredOptions.length > 0) {
            setOpen(true);
          }
        }}
        onClose={() => setOpen(false)}
        filterOptions={(options) => options}
        PopperComponent={StyledPopper}
        classes={{
          tag: darkTheme ? classes.tagDark : classes.tag,
          option: styles.option
        }}
        slotProps={darkTheme ? { paper: { sx: darkPaperSx } } : undefined}
        noOptionsText=""
        options={filteredOptions}
        value={selectedUsers}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
          // Show loader when user starts typing
          if (newInputValue && newInputValue.trim().length > 0) {
            setLoading(true);
          } else {
            setLoading(false);
          }
        }}
        onChange={(event, newValue) => {
          // Handle selection via Enter key or click
          if (newValue.length > selectedUsers.length) {
            const addedUser = newValue[newValue.length - 1];
            selectUserFromList(addedUser);
            // Clear the suggestion list after selection
            setUserList([]);
            setEmailToSearch('');
          }
        }}
        // MUI v5 signature is (props, option) — v4 passed the option first. The
        // props must be spread onto an <li> so MUI owns the click/keyboard
        // selection; that routes through onChange above, which does the
        // selecting and clearing (attaching onClick here too would add twice).
        renderOption={(optionProps, option) => {
          const { key, ...liProps } = optionProps;
          return (
            <li {...liProps} key={key || option?.id}>
              <PersonButton data={option} darkTheme={darkTheme} />
            </li>
          );
        }}
        renderTags={(values, getTagProps) => {
          const uniqueValues = values.filter((value, index, self) => index === self.findIndex((v) => v.id === value.id));
          return uniqueValues.map((option, index) => (
            <Chip
              variant="outlined"
              label={option?.email}
              {...getTagProps({ index })}
              deleteIcon={(
                <div aria-hidden="true">
                  <RemoveCircleIcon
                    onClick={(event) => {
                      removeUserFromList(option);
                    }}
                    className="MuiChip-deleteIcon"
                    style={{ margin: '0px 5px 0px 4px', color: '#EF4444' }}
                  />
                </div>
              )}
            />
          ));
        }}
        getOptionLabel={(option) => option?.email}
        renderInput={(params) => (
          <InputField
            disableUnderline
            {...params}
            autoComplete="off"
            variant="standard"
            placeholder={selectedUsers?.length === 0 ? inputPlaceholder : ''}
            autoFocus
            onChange={(e) => {
              searchUsers(e);
            }}
            onFocus={(e) => {
              // Clear the user list when input is focused and there's no search text
              if (!e.target.value || e.target.value.trim() === '') {
                setUserList([]);
                setEmailToSearch('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                // Check if user is selecting from dropdown (default autocomplete behavior)
                const dropdownElement = document.querySelector('.MuiAutocomplete-popper');
                const isDropdownVisible = dropdownElement && dropdownElement.style.display !== 'none';
                const hasOptions = uniqueUserList && uniqueUserList.length > 0;

                // Check if form submission is disabled
                const isFormDisabled = isDisabled || handlingAction || duplicateErrorType !== null || isSelfSelected || selectedUsers?.length <= 0;

                // Only trigger form submission if:
                // 1. Not selecting from dropdown OR
                // 2. Dropdown is not visible OR
                // 3. No options available in dropdown
                // 4. Form is not disabled
                if ((!isDropdownVisible || !hasOptions) && onEnterPress && !isFormDisabled) {
                  e.preventDefault();
                  onEnterPress();
                }
              }
            }}
            InputProps={{
              ...params.InputProps,
              // Leading search glyph only while nothing is picked yet — once chips
              // fill the field it would just crowd them.
              startAdornment: (
                <>
                  {selectedUsers?.length === 0 && (
                    <SearchRoundedIcon
                      style={{ color: darkTheme ? 'rgba(245,245,247,0.45)' : '#98a2b3', fontSize: 20, margin: '0 2px 0 4px' }}
                    />
                  )}
                  {params.InputProps.startAdornment}
                </>
              ),
              endAdornment: (
                <>
                  {loading && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: '12px'
                      }}
                    >
                      <div className={styles.spinnerLoader} />
                    </div>
                  )}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </div>
  );
}
